import {InfoRow, Typography} from '@sollapay/ui/components';
import {formatNIS, parseMoney} from '@sollapay/utils';
import {useTranslation} from 'react-i18next';

import {CreatePayoutStepLayout} from './CreatePayoutStepLayout';

import type {FC} from 'react';

export interface ConfirmationStepProps {
  payeeName: string;
  amount: string;
  /** Payout type label (e.g. Trustee, Seller, Buyer) — absent when the payee name already is the type (tax / land authority). */
  payeeType?: string;
  /** The free-text transfer purpose for a beneficiary, or the purpose-code label for an authority. */
  purpose?: string;
  /** The authority's own voucher number or payment reference, paired with the label for whichever it is. */
  reference?: {label: string; value: string};
}

export const ConfirmationStep: FC<ConfirmationStepProps> = ({
  payeeName,
  amount,
  payeeType,
  purpose,
  reference
}) => {
  const {t} = useTranslation('trustAccounts');
  const prefix = 'view.payouts.create.confirmation';

  return (
    <CreatePayoutStepLayout title={t(`${prefix}.title`)} subtitle={t(`${prefix}.subtitle`)}>
      <div className="flex flex-col items-start gap-3">
        <Typography size="sm" weight="semibold">
          {t(`${prefix}.summaryTitle`)}
        </Typography>
        <div className="flex w-full flex-col divide-y divide-border-subtle overflow-hidden rounded-xl border border-border">
          <div className="bg-background-secondary px-4 py-3.5">
            <InfoRow
              label={t(`${prefix}.amount`)}
              value={formatNIS(parseMoney(amount))}
              valueSize="md"
              valueWeight="bold"
            />
          </div>
          <div className="px-4 py-3.5">
            <InfoRow
              label={t(`${prefix}.recipient`)}
              value={payeeType ? `${payeeName} · ${payeeType}` : payeeName}
            />
          </div>
          {reference && (
            <div className="px-4 py-3.5">
              <InfoRow label={reference.label} value={reference.value} />
            </div>
          )}
          {purpose && (
            <div className="px-4 py-3.5">
              <InfoRow label={t(`${prefix}.purpose`)} value={purpose} />
            </div>
          )}
        </div>
      </div>
    </CreatePayoutStepLayout>
  );
};
